// src/modules/stock/BarCodeEmissionPage.js

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import JsBarcode from 'jsbarcode';
import { jsPDF } from 'jspdf';
import { db } from '../../firebaseConfig';
import {
    collection, getDocs, query, where, addDoc, orderBy,
    deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './Production.module.css';

// Reusable Components
import { Toast, TableSkeleton, EmptyState, PageHeader } from '../../components/common';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiDownload,
    FiSearch,
    FiPackage,
    FiClipboard,
    FiCalendar,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiPlus,
    FiUpload,
    FiPrinter,
    FiChevronUp,
    FiChevronDown,
    FiClock,
    FiUser,
    FiBox,
    FiDroplet,
    FiFolder,
    FiHome,
    FiBriefcase,
    FiTruck,
    FiShoppingCart,
    FiDollarSign,
    FiPercent,
    FiCreditCard,
    FiSend,
    FiFlag,
    FiCheckCircle,
    FiXCircle,
    FiClock as FiClockIcon,
    FiTrendingUp,
    FiUsers,
    FiPhone,
    FiMail,
    FiMap,
    FiNavigation,
    FiCalendar as FiCalendarIcon,
    FiMessageSquare,
    FiSettings,
    FiStar,
    FiHeart,
    FiThumbsUp,
    FiThumbsDown,
    FiMoreHorizontal,
    FiAward,
    FiDollarSign as FiMoney,
    FiArrowLeft as FiArrowLeftIcon,
    FiArrowRight as FiArrowRightIcon,
    FiPlusCircle,
    FiMinusCircle,
    FiInbox,
    FiExternalLink,
    FiHelpCircle,
    FiAlertTriangle,
    FiCheckCircle as FiCheckCircleIcon,
    FiClock as FiClockIcon2,
    FiTag as FiTagIcon,
    FiFlag as FiFlagIcon,
    FiUsers as FiUsersIcon,
    FiUser as FiUserIcon,
    FiCalendar as FiCalendarIcon2,
    FiMessageSquare as FiMessageSquareIcon,
    FiSend as FiSendIcon,
    FiPrinter as FiPrinterIcon
} from 'react-icons/fi';
import {
    MdEdit,
    MdDelete,
    MdAdd,
    MdSave,
    MdCancel,
    MdVisibility,
    MdPrint,
    MdLocationOn,
    MdBusiness,
    MdPerson,
    MdPhone,
    MdEmail,
    MdAttachMoney,
    MdReceipt,
    MdLocalShipping,
    MdCheckCircle,
    MdCancel as MdCancelIcon,
    MdPending,
    MdApproval,
    MdProductionQuantityLimits,
    MdDeliveryDining,
    MdFactCheck,
    MdMoney,
    MdMonetizationOn,
    MdOutlineCheckCircle,
    MdOutlineCancel,
    MdOutlinePending,
    MdInventory,
    MdHelp,
    MdSupport,
    MdAssignment,
    MdPeople,
    MdPersonOutline,
    MdCategory,
    MdPriorityHigh,
    MdAccessTime,
    MdAssignmentInd,
    MdQrCode,
    MdPrint as MdPrintIcon
} from 'react-icons/md';
import { FaBox, FaTruck, FaUser, FaBuilding, FaMoneyBillWave, FaClipboardList, FaDollarSign, FaPrint, FaCheck, FaTimes, FaEye, FaMapMarkerAlt, FaBoxOpen, FaWarehouse, FaTicketAlt, FaClock, FaFlag, FaUserTag, FaUsers, FaTasks, FaBarcode, FaQrcode } from 'react-icons/fa';

// ============================================================================
// CONSTANTS
// ============================================================================
const PRODUCTS_COLLECTION = 'products';
const PRODUCTION_ORDERS_COLLECTION = 'production-orders';
const BARCODE_HISTORY_COLLECTION = 'barcode-history';
const BOX_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-caixa'
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const BarCodeEmissionPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('emissao_de_etiqueta_de_codigo_de_barras');
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // Selection state (3-step wizard inside modal)
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [selectedBoxType, setSelectedBoxType] = useState(null);

    // Barcode history state
    const [barcodeHistory, setBarcodeHistory] = useState([]);

    // Toast notifications state
    const [toasts, setToasts] = useState([]);

    // Data fetching
    const { data: products, loading: productsLoading } = useFirestore(PRODUCTS_COLLECTION);
    const { data: boxTypes, loading: boxTypesLoading } = useSubCollection(
        BOX_TYPE_COLLECTION_PATH.parentCollection,
        BOX_TYPE_COLLECTION_PATH.parentId,
        BOX_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );

    // State for completed orders
    const [completedOrders, setCompletedOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);

    // Barcode preview ref
    const barcodeContainerRef = useRef(null);

    // Toast notification helper
    const showToast = useCallback((message, type = 'info', duration = 5000) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type, duration }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(n => n.id !== id));
    }, []);

    // ============================================================================
    // LOAD BARCODE HISTORY
    // ============================================================================
    const loadBarcodeHistory = useCallback(async () => {
        setIsLoadingHistory(true);
        try {
            const historyRef = collection(db, BARCODE_HISTORY_COLLECTION);
            const q = query(historyRef, orderBy('generatedAt', 'desc'));
            const snapshot = await getDocs(q);

            const history = [];
            snapshot.forEach(doc => {
                history.push({ id: doc.id, ...doc.data() });
            });

            setBarcodeHistory(history);
        } catch (error) {
            console.error("Error loading barcode history:", error);
            showToast(t('pages.barcode.loadingError'), 'error');
        } finally {
            setIsLoadingHistory(false);
        }
    }, [showToast, t]);

    // Load history on mount
    useEffect(() => {
        loadBarcodeHistory();
    }, [loadBarcodeHistory]);

    // ============================================================================
    // FILTER ACTIVE PRODUCTS
    // ============================================================================
    const activeProducts = useMemo(() => {
        if (!products) return [];
        return products
            .filter(p => p.situation === 'Ativo')
            .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    }, [products]);

    const productOptions = useMemo(() => {
        return activeProducts.map(p => ({
            value: p.id,
            label: `${p.code} - ${p.commercialName}`,
            ...p
        }));
    }, [activeProducts]);

    // ============================================================================
    // FETCH COMPLETED ORDERS FOR SELECTED PRODUCT
    // ============================================================================
    useEffect(() => {
        const fetchCompletedOrders = async () => {
            if (!selectedProduct) {
                setCompletedOrders([]);
                return;
            }

            setOrdersLoading(true);
            try {
                const ordersRef = collection(db, PRODUCTION_ORDERS_COLLECTION);
                const q = query(where('status', '==', 'completed'));
                const snapshot = await getDocs(q);

                const orders = [];
                snapshot.forEach(doc => {
                    const data = doc.data();
                    const matchingItem = data.items?.find(
                        item => item.productId === selectedProduct.value || item.productId === selectedProduct.id
                    );

                    if (matchingItem) {
                        orders.push({
                            id: doc.id,
                            opCode: data.opCode,
                            productId: matchingItem.productId,
                            productCode: matchingItem.productCode,
                            productName: matchingItem.productName,
                            quantity: matchingItem.quantity,
                            mode: matchingItem.mode,
                            lote: matchingItem.lote,
                            completedAt: data.completedAt,
                            createdAt: data.createdAt,
                            fabricationDate: data.fabricationDate,
                            expirationDate: data.expirationDate,
                        });
                    }
                });

                orders.sort((a, b) => {
                    const dateA = a.completedAt ? new Date(a.completedAt) : (a.createdAt?.toDate?.() || 0);
                    const dateB = b.completedAt ? new Date(b.completedAt) : (b.createdAt?.toDate?.() || 0);
                    return dateB - dateA;
                });

                setCompletedOrders(orders);
            } catch (error) {
                console.error("Error fetching completed orders:", error);
                showToast(t('pages.barcode.fetchOrdersError'), 'error');
            } finally {
                setOrdersLoading(false);
            }
        };

        fetchCompletedOrders();
    }, [selectedProduct, showToast, t]);

    // ============================================================================
    // FILTER BOX TYPES BY PRODUCT (using boxTypeIds from product.technical)
    // ============================================================================
    const filteredBoxTypes = useMemo(() => {
        if (!boxTypes || !selectedProduct) return [];

        const product = activeProducts.find(p => p.id === selectedProduct.value);
        if (!product) return [];

        // PRIORITY 1: Check if product has specific boxTypeIds defined in technical
        const productBoxTypeIds = product.technical?.boxTypeIds;

        if (productBoxTypeIds && Array.isArray(productBoxTypeIds) && productBoxTypeIds.length > 0) {
            const filtered = boxTypes.filter(box => productBoxTypeIds.includes(box.id));
            if (filtered.length > 0) {
                return filtered.sort((a, b) => a.name.localeCompare(b.name));
            }
        }

        // PRIORITY 2: Fallback to filtering by product type IDs
        if (!product.productTypeIds || product.productTypeIds.length === 0) {
            return [];
        }

        return boxTypes.filter(box =>
            box.productTypeId && product.productTypeIds.includes(box.productTypeId)
        ).sort((a, b) => a.name.localeCompare(b.name));
    }, [boxTypes, selectedProduct, activeProducts]);

    // ============================================================================
    // GENERATE BARCODE ON BATCH SELECTION
    // ============================================================================
    useEffect(() => {
        if (!selectedBatch || !barcodeContainerRef.current) return;

        const barcodeValue = selectedBatch.lote || selectedBatch.opCode;

        try {
            barcodeContainerRef.current.innerHTML = '';
            const canvas = document.createElement('canvas');
            barcodeContainerRef.current.appendChild(canvas);

            JsBarcode(canvas, barcodeValue, {
                format: 'CODE128',
                width: 2,
                height: 50,
                displayValue: true,
                fontSize: 14,
                margin: 10,
            });
        } catch (error) {
            console.error("Error generating barcode:", error);
        }
    }, [selectedBatch]);

    // ============================================================================
    // HELPER FUNCTIONS
    // ============================================================================
    const formatDate = (dateValue) => {
        if (!dateValue) return '—';
        if (dateValue.toDate) {
            return dateValue.toDate().toLocaleDateString('pt-BR');
        }
        if (typeof dateValue === 'string' && dateValue.includes('/')) return dateValue;
        if (typeof dateValue === 'string' && dateValue.includes('-')) {
            const [year, month, day] = dateValue.split('-');
            return `${day}/${month}/${year}`;
        }
        return '—';
    };

    const formatDateTime = (timestamp) => {
        if (!timestamp?.toDate) return '—';
        const date = timestamp.toDate();
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatFirestoreDate = (dateValue) => {
        if (!dateValue) return '—';
        if (dateValue.toDate) {
            return dateValue.toDate().toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        if (typeof dateValue === 'string') return dateValue;
        return '—';
    };

    const calculateBoxes = useCallback(() => {
        if (!selectedBatch || !selectedBoxType) return 0;

        let totalUnits = selectedBatch.mode === 'units' ? selectedBatch.quantity : 0;

        if (selectedBatch.mode === 'mass') {
            const product = activeProducts.find(p => p.id === selectedBatch.productId);
            const gramatura = parseFloat(product?.technical?.gramatura) || 0;
            const unit = product?.technical?.gramaturaUnit || 'g';
            const gramaturaKg = (unit === 'g' || unit === 'ml') ? gramatura / 1000 : gramatura;
            totalUnits = gramaturaKg > 0 ? Math.floor(selectedBatch.quantity / gramaturaKg) : 0;
        }

        return Math.ceil(totalUnits / selectedBoxType.quantityPerBox);
    }, [selectedBatch, selectedBoxType, activeProducts]);

    // ============================================================================
    // SAVE TO HISTORY
    // ============================================================================
    const saveToHistory = useCallback(async (batchNumber, productName, productCode, boxTypeName, boxesGenerated, pdfBlob = null) => {
        try {
            const historyEntry = {
                batchNumber,
                productName,
                productCode,
                boxTypeName,
                boxesGenerated,
                generatedAt: serverTimestamp(),
                generatedBy: 'user',
            };

            if (pdfBlob) {
                const reader = new FileReader();
                reader.readAsDataURL(pdfBlob);
                await new Promise((resolve) => {
                    reader.onloadend = async () => {
                        historyEntry.pdfData = reader.result;
                        await addDoc(collection(db, BARCODE_HISTORY_COLLECTION), historyEntry);
                        resolve();
                    };
                });
            } else {
                await addDoc(collection(db, BARCODE_HISTORY_COLLECTION), historyEntry);
            }

            await loadBarcodeHistory();
        } catch (error) {
            console.error("Error saving to history:", error);
        }
    }, [loadBarcodeHistory]);

    // ============================================================================
    // DELETE FROM HISTORY
    // ============================================================================
    const deleteHistoryEntry = useCallback(async (historyId) => {
        try {
            await deleteDoc(doc(db, BARCODE_HISTORY_COLLECTION, historyId));
            showToast(t('pages.barcode.deleteSuccess'), 'success');
            await loadBarcodeHistory();
        } catch (error) {
            console.error("Error deleting history entry:", error);
            showToast(t('pages.barcode.deleteError'), 'error');
        }
    }, [loadBarcodeHistory, showToast, t]);

    // ============================================================================
    // REGENERATE BARCODE PDF
    // ============================================================================
    const regenerateBarcode = useCallback(async (historyEntry) => {
        setIsGenerating(true);

        try {
            if (historyEntry.pdfData) {
                const link = document.createElement('a');
                link.href = historyEntry.pdfData;
                link.download = `etiquetas_${historyEntry.batchNumber}_${historyEntry.boxTypeName}_regen.pdf`;
                link.click();
                showToast(t('pages.barcode.regenerateSuccess', { batchNumber: historyEntry.batchNumber }), 'success');
                setIsGenerating(false);
                return;
            }

            showToast(
                t('pages.barcode.regenerateWarning'),
                'warning',
                5000
            );

        } catch (error) {
            console.error("Error regenerating barcode:", error);
            showToast(t('pages.barcode.regenerateError'), 'error');
        } finally {
            setIsGenerating(false);
        }
    }, [showToast, t]);

    // ============================================================================
    // RESET FORM
    // ============================================================================
    const resetForm = useCallback(() => {
        setSelectedProduct(null);
        setSelectedBatch(null);
        setSelectedBoxType(null);
        if (barcodeContainerRef.current) {
            barcodeContainerRef.current.innerHTML = '';
        }
    }, []);

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    const handleOpenModal = () => {
        resetForm();
        setShowModal(true);
    };

    // ============================================================================
    // GENERATE PDF
    // ============================================================================
    const handleGeneratePDF = async () => {
        if (!selectedProduct || !selectedBatch || !selectedBoxType) {
            showToast(t('pages.barcode.completeAllSteps'), 'warning');
            return;
        }

        setIsGenerating(true);

        try {
            const barcodeCanvas = barcodeContainerRef.current?.querySelector('canvas');
            if (!barcodeCanvas) {
                throw new Error('Código de barras não gerado');
            }

            const barcodeDataUrl = barcodeCanvas.toDataURL('image/png');
            const productName = selectedBatch.productName || selectedProduct.commercialName;
            const productCode = selectedBatch.productCode || selectedProduct.code;
            const batchNumber = selectedBatch.lote || selectedBatch.opCode;
            const fabricationDate = selectedBatch.fabricationDate;
            const expirationDate = selectedBatch.expirationDate;
            const boxes = calculateBoxes();
            const quantityPerBox = selectedBoxType.quantityPerBox;

            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const labelsPerRow = 2;
            const labelsPerColumn = 4;
            const labelsPerPage = labelsPerRow * labelsPerColumn;
            const labelWidth = (pageWidth - 20) / labelsPerRow;
            const labelHeight = (pageHeight - 20) / labelsPerColumn;

            for (let labelIndex = 0; labelIndex < boxes; labelIndex++) {
                const pageIndex = Math.floor(labelIndex / labelsPerPage);
                const positionOnPage = labelIndex % labelsPerPage;
                const col = positionOnPage % labelsPerRow;
                const row = Math.floor(positionOnPage / labelsPerRow);
                const x = 10 + (col * labelWidth);
                const y = 10 + (row * labelHeight);

                if (labelIndex > 0 && positionOnPage === 0) {
                    pdf.addPage();
                }

                pdf.setDrawColor(200, 200, 200);
                pdf.rect(x, y, labelWidth, labelHeight);

                pdf.setFontSize(8);
                pdf.setTextColor(150, 150, 150);
                pdf.text('COSMETICS LAB', x + 5, y + 5);

                pdf.setFontSize(11);
                pdf.setTextColor(0, 0, 0);
                pdf.setFont(undefined, 'bold');
                const productNameLines = pdf.splitTextToSize(productName, labelWidth - 10);
                pdf.text(productNameLines, x + 5, y + 12);

                pdf.setFontSize(8);
                pdf.setFont(undefined, 'normal');
                pdf.text(`${t('pages.barcode.previewCode')} ${productCode}`, x + 5, y + 22);

                pdf.text(`${t('pages.barcode.previewLote')} ${batchNumber}`, x + 5, y + 28);
                pdf.text(`Fab.: ${formatDate(fabricationDate)}`, x + 5, y + 34);
                pdf.text(`Val.: ${formatDate(expirationDate)}`, x + 5, y + 40);

                pdf.text(`${t('pages.barcode.previewBoxQty')} ${quantityPerBox} ${t('pages.barcode.units')}`, x + 5, y + 46);
                pdf.text(`${t('pages.barcode.previewBox')} ${labelIndex + 1} ${t('common.of')} ${boxes}`, x + labelWidth - 35, y + 46);

                const barcodeWidth = labelWidth - 20;
                const barcodeHeight = 18;
                const barcodeX = x + (labelWidth - barcodeWidth) / 2;
                const barcodeY = y + 52;
                pdf.addImage(barcodeDataUrl, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);

                pdf.setFontSize(6);
                pdf.setTextColor(100, 100, 100);
                pdf.text(batchNumber, x + labelWidth / 2, barcodeY + barcodeHeight + 4, { align: 'center' });
            }

            const pdfBlob = pdf.output('blob');
            const fileName = `etiquetas_${batchNumber}_${selectedBoxType.name}.pdf`;
            pdf.save(fileName);

            await saveToHistory(
                batchNumber,
                productName,
                productCode,
                selectedBoxType.name,
                boxes,
                pdfBlob
            );

            showToast(t('pages.barcode.generateSuccess', { boxes }), 'success');
            handleCloseModal();

        } catch (error) {
            console.error("Error generating PDF:", error);
            showToast(t('pages.barcode.generateError'), 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    // ============================================================================
    // RENDER HISTORY TABLE
    // ============================================================================
    const renderHistoryTable = () => {
        if (isLoadingHistory) {
            return (
                <div className={styles.tableContainer}>
                    <TableSkeleton rows={5} columns={7} />
                </div>
            );
        }

        if (barcodeHistory.length === 0) {
            return (
                <div className={styles.tableContainer}>
                    <EmptyState
                        title={t('pages.barcode.noHistory')}
                        message={t('pages.barcode.noHistoryMessage')}
                    />
                </div>
            );
        }

        return (
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>{t('pages.barcode.tableDate')}</th>
                            <th>{t('pages.barcode.tableBatch')}</th>
                            <th>{t('pages.barcode.tableProduct')}</th>
                            <th>{t('pages.barcode.tableCode')}</th>
                            <th>{t('pages.barcode.tableBoxType')}</th>
                            <th>{t('pages.barcode.tableLabels')}</th>
                            <th style={{ textAlign: 'center', width: '100px' }}>{t('pages.barcode.tableActions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {barcodeHistory.map((item) => (
                            <tr key={item.id} className={styles.tableRow}>
                                <td>{formatFirestoreDate(item.generatedAt)}</td>
                                <td>
                                    <strong>{item.batchNumber}</strong>
                                </td>
                                <td>{item.productName}</td>
                                <td>{item.productCode}</td>
                                <td>{item.boxTypeName}</td>
                                <td style={{ textAlign: 'center' }}>
                                    <span className={styles.countBadge} style={{
                                        backgroundColor: '#17a2b8',
                                        borderRadius: '12px',
                                        width: 'auto',
                                        padding: '2px 10px'
                                    }}>
                                        {item.boxesGenerated} {t('pages.barcode.labels')}
                                    </span>
                                </td>
                                <td>
                                    <div className={styles.actionsContainer} style={{ justifyContent: 'center' }}>
                                        {can.generate && (
                                            <button
                                                className={`${styles.actionButton} ${styles.regenerateButton}`}
                                                onClick={() => regenerateBarcode(item)}
                                                disabled={isGenerating}
                                                title={t('pages.barcode.regenerate')}
                                            >
                                                <FiRefreshCw size={14} />
                                            </button>
                                        )}
                                        {can.delete && (
                                            <button
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                onClick={() => {
                                                    if (window.confirm(t('pages.barcode.deleteConfirm', { batchNumber: item.batchNumber }))) {
                                                        deleteHistoryEntry(item.id);
                                                    }
                                                }}
                                                title={t('pages.barcode.delete')}
                                            >
                                                <FiTrash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // ============================================================================
    // RENDER MODAL CONTENT
    // ============================================================================
    const renderModalContent = () => {
        return (
            <div className={styles.createModalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FaBarcode size={24} style={{ marginRight: '10px' }} /> {t('pages.barcode.modalTitle')}</h2>
                        <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '4px' }}>
                            {t('pages.barcode.modalSubtitle')}
                        </p>
                    </div>
                    <button
                        onClick={handleCloseModal}
                        className={styles.cancelButton}
                        style={{ padding: '8px 16px' }}
                    >
                        <FiX size={18} /> {t('common.close')}
                    </button>
                </div>

                {/* Step 1 - Product Selection */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.stepBadge}>1</div>
                        <h3 className={styles.sectionTitle}>{t('pages.barcode.stepProduct')}</h3>
                        {selectedProduct && <span className={styles.stepCheck}><FiCheck size={16} style={{ color: '#28a745' }} /></span>}
                    </div>
                    <div className={styles.formGroup}>
                        <Select
                            options={productOptions}
                            value={selectedProduct}
                            onChange={setSelectedProduct}
                            placeholder={t('pages.barcode.selectProduct')}
                            isClearable
                            isLoading={productsLoading}
                            className={styles.productSelect}
                            classNamePrefix="react-select"
                        />
                        {selectedProduct && (
                            <div className={styles.selectedInfo}>
                                <span className={styles.infoTag}>
                                    <FiPackage size={12} style={{ marginRight: '4px' }} /> {selectedProduct.code}
                                </span>
                                <span className={styles.infoTag}>
                                    <FiTag size={12} style={{ marginRight: '4px' }} /> {selectedProduct.commercialName}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 2 - Batch Selection */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.stepBadge}>2</div>
                        <h3 className={styles.sectionTitle}>{t('pages.barcode.stepBatch')}</h3>
                        {selectedBatch && <span className={styles.stepCheck}><FiCheck size={16} style={{ color: '#28a745' }} /></span>}
                    </div>
                    <div className={styles.formGroup}>
                        {!selectedProduct ? (
                            <div className={styles.infoMessage}><FiAlertCircle size={16} style={{ marginRight: '8px' }} /> {t('pages.barcode.selectProductFirst')}</div>
                        ) : ordersLoading ? (
                            <div className={styles.loadingMessage}><FiRefreshCw size={16} className={styles.buttonSpinner} style={{ marginRight: '8px' }} /> {t('common.loading')}</div>
                        ) : completedOrders.length === 0 ? (
                            <div className={styles.infoMessage}><FiInbox size={16} style={{ marginRight: '8px' }} /> {t('pages.barcode.noCompletedOrders')}</div>
                        ) : (
                            <div className={styles.batchList}>
                                {completedOrders.map(order => (
                                    <div
                                        key={order.id}
                                        className={`${styles.batchItem} ${selectedBatch?.id === order.id ? styles.batchItemSelected : ''}`}
                                        onClick={() => setSelectedBatch(order)}
                                    >
                                        <div className={styles.batchItemMain}>
                                            <span className={styles.batchCode}>{order.opCode}</span>
                                            <span className={styles.batchLote}>
                                                {t('pages.barcode.batchLote')}: {order.lote || '—'}
                                            </span>
                                            <span className={styles.batchQty}>
                                                {order.quantity} {order.mode === 'mass' ? 'Kg' : t('pages.barcode.units')}
                                            </span>
                                        </div>
                                        <div className={styles.batchItemDate}>
                                            {t('common.completed')}: {formatDateTime(order.completedAt)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 3 - Box Type Selection */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.stepBadge}>3</div>
                        <h3 className={styles.sectionTitle}>{t('pages.barcode.stepBoxType')}</h3>
                        {selectedBoxType && <span className={styles.stepCheck}><FiCheck size={16} style={{ color: '#28a745' }} /></span>}
                    </div>
                    <div className={styles.formGroup}>
                        {!selectedProduct ? (
                            <div className={styles.infoMessage}><FiAlertCircle size={16} style={{ marginRight: '8px' }} /> {t('pages.barcode.selectProductFirst')}</div>
                        ) : boxTypesLoading ? (
                            <div className={styles.loadingMessage}><FiRefreshCw size={16} className={styles.buttonSpinner} style={{ marginRight: '8px' }} /> {t('common.loading')}</div>
                        ) : filteredBoxTypes.length === 0 ? (
                            <div className={styles.infoMessage}>
                                <FiAlertCircle size={16} style={{ marginRight: '8px' }} />
                                {t('pages.barcode.noBoxTypes')}
                                {selectedProduct.technical?.boxTypeIds ? (
                                    <span style={{ display: 'block', marginTop: '8px', fontSize: '0.85rem' }}>
                                        {t('pages.barcode.noBoxTypesHintSpecific')}
                                    </span>
                                ) : (
                                    <span style={{ display: 'block', marginTop: '8px', fontSize: '0.85rem' }}>
                                        {t('pages.barcode.noBoxTypesHint')}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div className={styles.boxTypeGrid}>
                                {filteredBoxTypes.map(box => (
                                    <div
                                        key={box.id}
                                        className={`${styles.boxTypeCard} ${selectedBoxType?.id === box.id ? styles.boxTypeCardSelected : ''}`}
                                        onClick={() => setSelectedBoxType(box)}
                                    >
                                        <div className={styles.boxTypeName}>{box.name}</div>
                                        <div className={styles.boxTypeQty}>
                                            <FiBox size={12} style={{ marginRight: '4px' }} /> {box.quantityPerBox} {t('pages.barcode.unitsPerBox')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Preview Section */}
                {selectedProduct && selectedBatch && selectedBoxType && (
                    <div className={styles.previewSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.stepBadge}><FiEye size={16} /></div>
                            <h3 className={styles.sectionTitle}>{t('pages.barcode.stepPreview')}</h3>
                        </div>
                        <div className={styles.previewContainer}>
                            <div className={styles.previewLabel}>
                                <div className={styles.previewLabelHeader}>
                                    <span className={styles.previewBrand}>{t('pages.barcode.previewBrand')}</span>
                                </div>
                                <div className={styles.previewProduct}>
                                    {selectedBatch.productName || selectedProduct.commercialName}
                                </div>
                                <div className={styles.previewDetails}>
                                    <div className={styles.previewRow}>
                                        <span>{t('pages.barcode.previewCode')}</span>
                                        <strong>{selectedProduct.code}</strong>
                                    </div>
                                    <div className={styles.previewRow}>
                                        <span>{t('pages.barcode.previewLote')}</span>
                                        <strong>{selectedBatch.lote || selectedBatch.opCode}</strong>
                                    </div>
                                    <div className={styles.previewRow}>
                                        <span>{t('pages.barcode.previewExpiration')}</span>
                                        <strong>{formatDate(selectedBatch.expirationDate)}</strong>
                                    </div>
                                    <div className={styles.previewRow}>
                                        <span>{t('pages.barcode.previewBox')}</span>
                                        <strong>{selectedBoxType.name}</strong>
                                    </div>
                                </div>
                                <div
                                    className={styles.previewBarcode}
                                    ref={barcodeContainerRef}
                                ></div>
                                <div className={styles.previewFooter}>
                                    <span><FiBox size={12} style={{ marginRight: '4px' }} /> {selectedBoxType.quantityPerBox} {t('pages.barcode.unitsPerBox')}</span>
                                    <span><FiPackage size={12} style={{ marginRight: '4px' }} /> {calculateBoxes()} {t('pages.barcode.previewTotalBoxes')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal Actions */}
                <div className={styles.formActions}>
                    <button
                        onClick={handleCloseModal}
                        className={styles.cancelButton}
                        disabled={isGenerating}
                    >
                        <FiX size={18} /> {t('common.cancel')}
                    </button>
                    <button
                        onClick={handleGeneratePDF}
                        className={styles.confirmButton}
                        disabled={!selectedProduct || !selectedBatch || !selectedBoxType || isGenerating}
                    >
                        {isGenerating ? (
                            <><FiRefreshCw size={18} className={styles.buttonSpinner} /> {t('pages.barcode.generating')}</>
                        ) : (
                            <><FiFileText size={18} /> {t('pages.barcode.generateButton')}</>
                        )}
                    </button>
                </div>
            </div>
        );
    };

    // ============================================================================
    // MAIN RENDER
    // ============================================================================
    return (
        <div className={styles.pageContainer}>

            {/* Toast Notifications - Using reusable Toast component */}
            <div className={styles.toastContainer}>
                {toasts.map(n => (
                    <Toast
                        key={n.id}
                        message={n.message}
                        type={n.type}
                        duration={n.duration}
                        onClose={() => removeToast(n.id)}
                    />
                ))}
            </div>

            {/* Page Header - Using reusable PageHeader component */}
            <PageHeader
                title={t('pages.barcode.title')}
                actionText={can.create ? t('pages.barcode.newEmission') : undefined}
                onAction={can.create ? handleOpenModal : undefined}
                showAction={can.create}
            />

            {/* History Table */}
            <div style={{ marginTop: '24px' }}>
                <h2 className={styles.sectionTitle}>
                    <FiClock size={20} style={{ marginRight: '10px' }} /> {t('pages.barcode.history')}
                    {barcodeHistory.length > 0 && (
                        <span className={styles.countBadge}>{barcodeHistory.length}</span>
                    )}
                </h2>
                {renderHistoryTable()}
            </div>

            {/* Modal */}
            {showModal && (
                <div className={styles.modalBackdrop}>
                    {renderModalContent()}
                </div>
            )}
        </div>
    );
};

export default BarCodeEmissionPage;